import React, { useEffect, useState } from 'react';
import { Card, Badge, Col, Row, Button, Placeholder } from 'react-bootstrap';
import PropTypes from 'prop-types';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';
import ModalSeguroCancelarSuscripcion from './Modales/ModalSeguroCancelarSuscripcion';
import ModalAgregarProductoSuscripcion from './Modales/ModalAgregarProductoSuscripcion';
import ModalConfirmarPagoManual from './Modales/ModalConfirmarPagoManual';

const SubscriptionTitle = ({ idSuscripcion, infoSuscripcion, onRefreshInfo }) => {
    const { token, idCatTipoUsuario } = useToken();
    const isAdmin = idCatTipoUsuario === 1 || idCatTipoUsuario === '1';

    const [suscripcion, setSuscripcion] = useState({
        titulo: '',
        estado: ''
    });

    // Detectamos si la información principal aún no se carga
    const isLoading = suscripcion.titulo === '' && suscripcion.estado === '';

    const [showModalCancel, setShowModalCancel] = useState(false);
    const [showModalAgregarProducto, setShowModalAgregarProducto] = useState(false);
    const [showModalPagoManual, setShowModalPagoManual] = useState(false);
    const [estadoPagoSuscripcion, setEstadoPagoSuscripcion] = useState(null);
    const [procesandoPago, setProcesandoPago] = useState(false);
    const { showErrorToast, showSuccessToast } = useToast();
    const [cancelando, setCancelando] = useState(false);

    const handleRealizarPago = async () => {
        if (!idSuscripcion) return;
        try {
            const response = await fetch(`/api/Suscripciones/ObtenerEstadoPagoSuscripcion?idSuscripcion=${idSuscripcion}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const estado = await response.json();
                setEstadoPagoSuscripcion(estado);
                setShowModalPagoManual(true);
            } else {
                showErrorToast('Error al obtener el estado de pago de la suscripción');
            }
        } catch (error) {
            console.error('Error al obtener estado de pago:', error);
            showErrorToast('Error de red al obtener estado de pago');
        }
    };

    const handleConfirmarPagoManual = async (dataForm) => {
        if (!infoSuscripcion) return;

        try {
            setProcesandoPago(true);

            let archivoBase64 = null;
            let nombreArchivo = null;
            let tipoArchivo = null;

            if (dataForm.vArchivo) {
                const file = dataForm.vArchivo;
                nombreArchivo = file.name;
                tipoArchivo = file.type;

                const reader = new FileReader();
                archivoBase64 = await new Promise((resolve, reject) => {
                    reader.onload = () => {
                        const base64 = reader.result.split(',')[1];
                        resolve(base64);
                    };
                    reader.onerror = reject;
                    reader.readAsDataURL(file);
                });
            }

            const response = await fetch('/api/Suscripciones/RealizarPagoManualSuscripcionOtrosMetodos', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    idSuscripcion: infoSuscripcion.idSuscripcion || parseInt(idSuscripcion),
                    idOrden: 0,
                    vUUIDSuscripcion: infoSuscripcion.vUUIDSuscripcion || infoSuscripcion.StripeId,
                    vStripeSubscriptionId: infoSuscripcion.vStripeSubscriptionId || infoSuscripcion.StripeId,
                    vMoneda: infoSuscripcion.vMoneda || 'MXN',
                    dImportePagado: Number.parseFloat(dataForm.dImportePagado),
                    idCatFormaPago: Number.parseInt(dataForm.idCatFormaPago),
                    dFechaPago: dataForm.dFechaPago,
                    vReferencia: dataForm.vReferencia,
                    vJsonDetallesLinkPago: JSON.stringify(dataForm.vJsonDetallesLinkPago || {}),
                    vArchivoBase64: archivoBase64,
                    vNombreArchivo: nombreArchivo,
                    vTipoArchivo: tipoArchivo
                })
            });

            if (response.ok) {
                const data = await response.json();
                if (data.iResponseType === 1) {
                    showSuccessToast('Pago manual registrado correctamente');
                    setShowModalPagoManual(false);
                    await ObtenerTituloInfo();
                } else {
                    showErrorToast(data.vMessage || 'No fue posible registrar el pago');
                }
            } else {
                showErrorToast(`${response.status} ${response.statusText}`);
            }
        } catch (error) {
            console.error('Error al procesar pago:', error);
            showErrorToast('Error al procesar pago: ' + error.message);
        } finally {
            setProcesandoPago(false);
        }
    };

    const cancelarSuscripcion = async (bCancelInStripe = true) => {
        if (!idSuscripcion) return;

        try {
            setCancelando(true);

            const response = await fetch('/api/Suscripciones/CancelarSuscripcion', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    idSuscripcion: parseInt(idSuscripcion),
                    motivoCancelacion: 'Cancelación solicitada desde el panel',
                    bCancelInStripe: bCancelInStripe
                })
            });

            if (response.ok) {
                const data = await response.json();
                if (data.iResponseType === 1) {
                    showSuccessToast(data.vMessage || 'Suscripción cancelada correctamente');
                    await ObtenerTituloInfo(); // refrescar info
                } else {
                    showErrorToast(data.vMessage || 'No fue posible cancelar la suscripción');
                }
            } else {
                showErrorToast(`${response.status} ${response.statusText}`);
            }
        } catch (error) {
            showErrorToast('Error al cancelar la suscripción: ' + error.message);
        } finally {
            setCancelando(false);
            setShowModalCancel(false);
        }
    };

    //////
    const ObtenerTituloInfo = async () => {
        if (!idSuscripcion) return;

        try {
            const response = await fetch(`/api/Suscripciones/ObtenerTituloInfo`, {
                method: "POST",
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ idSuscripcion: parseInt(idSuscripcion) })
            });

            if (response.ok) {
                const data = await response.json();

                if (data.iResponseType === 1) {

                    const ObjDetalles = JSON.parse(data.detalles);
                    const detalles = ObjDetalles.Table1 || ObjDetalles.table1 || ObjDetalles.Table2 || ObjDetalles.table2 || [];

                    if (detalles.length > 0) {
                        console.log("Datos recibidos del SP GetNombre:", detalles[0]);
                        setSuscripcion({
                            titulo: detalles[0].vNombreSuscripcion,
                            estado: detalles[0].vEstadoSuscripcion
                        });
                        // No seteamos suscripcionCompleta aqui, lo hacemos en ObtenerSuscripcionCompleta
                    } else {
                        console.log("No se encontró el título");
                    }
                } else {
                    console.log("Error al obtener datos:", data.vMessage || data);
                }
            } else {
                console.log("Error en la respuesta del servidor");
            }
        } catch (error) {
            console.error("Error de conexión", error);
            console.log("Error de conexión");
        }
    };

    useEffect(() => {
        if (idSuscripcion) {
            ObtenerTituloInfo();
        }
    }, [idSuscripcion]);
    /////////

    const getStatusColor = (status) => {
        if (!status) return "secondary";
        const s = status.toUpperCase();
        if (s === 'ACTIVA') return 'success';
        if (s === 'CANCELADA' || s === 'INACTIVA') return 'danger';
        return 'danger';
    };

    return (
        <>
            <Card className="border-0 shadow-sm mb-3">
                <Card.Body>
                    <Row className="align-items-center">
                        <Col xs={12} md={5} lg={6}>
                            <Row className="align-items-center mb-2">
                                <Col xs="auto" className="pe-1">
                                    <span className="border text-muted rounded px-2 py-1" style={{ fontSize: '0.85rem', fontWeight: 600 }}>
                                        #{idSuscripcion}
                                    </span>
                                </Col>
                                <Col xs="auto" className="ps-1">
                                    {isLoading ? (
                                        <Placeholder as="span" animation="glow">
                                            <Placeholder className="rounded px-2 py-1" style={{ width: '80px', height: '24px' }} bg="secondary" />
                                        </Placeholder>
                                    ) : suscripcion.estado && (
                                        <Badge bg={getStatusColor(suscripcion.estado)} className="px-2 py-1 text-uppercase" style={{ fontSize: '0.85rem', fontWeight: 600 }}>
                                            {suscripcion.estado.toLowerCase() === 'inactiva' ? 'Cancelada' : suscripcion.estado}
                                        </Badge>
                                    )}
                                </Col>
                            </Row>
                            <Row>
                                <Col>
                                    <Card.Title className="mb-0" style={{ fontWeight: 700 }}>
                                        {isLoading ? (
                                            <Placeholder as="span" animation="glow">
                                                <Placeholder xs={8} bg="secondary" className="rounded" />
                                            </Placeholder>
                                        ) : suscripcion.titulo}
                                    </Card.Title>
                                </Col>
                            </Row>
                        </Col>
                        <Col xs={12} md={7} lg={6} className="mt-3 mt-md-0">
                            {suscripcion?.estado?.toLowerCase() === 'activa' && (
                                <Row className="justify-content-md-end g-2">
                                    <Col xs={12} md="auto">
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            className="fw-bold px-3 rounded-3 w-100 text-nowrap"
                                            style={{ fontSize: '0.85rem' }}
                                            onClick={() => setShowModalCancel(true)}
                                        >
                                            Cancelar Suscripción
                                        </Button>
                                    </Col>
                                    <Col xs={12} md="auto">
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            className="fw-bold px-3 rounded-3 w-100 text-nowrap"
                                            style={{ fontSize: '0.85rem' }}
                                            onClick={() => setShowModalAgregarProducto(true)}
                                        >
                                            Agregar Producto
                                        </Button>
                                    </Col>
                                    <Col xs={12} md="auto">
                                        <Button
                                            variant="primary"
                                            size="sm"
                                            className="fw-bold px-3 rounded-3 w-100 text-nowrap shadow-sm"
                                            style={{ fontSize: '0.85rem' }}
                                            onClick={handleRealizarPago}
                                        >
                                            Realizar Pago
                                        </Button>
                                    </Col>
                                </Row>
                            )}
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <ModalSeguroCancelarSuscripcion
                show={showModalCancel}
                onClose={() => setShowModalCancel(false)}
                onConfirm={cancelarSuscripcion}
                processing={cancelando}
                suscripcionInfo={{
                    vNombreSuscripcion: suscripcion.titulo,
                    idSuscripcion: idSuscripcion
                }}
                isAdmin={isAdmin}
            />

            {infoSuscripcion && (
                <ModalAgregarProductoSuscripcion
                    show={showModalAgregarProducto}
                    onHide={() => setShowModalAgregarProducto(false)}
                    suscripcion={{
                        idSuscripcion: idSuscripcion,
                        idLicencia: infoSuscripcion.idLicencia,
                        vTipoRenovacion: infoSuscripcion.vTipoRenovacion,
                        vMoneda: infoSuscripcion.vMoneda,
                        vStripeSubscriptionId: infoSuscripcion.vStripeSubscriptionId || infoSuscripcion.StripeId
                    }}
                    onCreated={() => {
                        ObtenerTituloInfo();
                        if (onRefreshInfo) onRefreshInfo();
                    }}
                />
            )}

            {showModalPagoManual && (
                <ModalConfirmarPagoManual
                    show={showModalPagoManual}
                    onHide={() => setShowModalPagoManual(false)}
                    suscripcion={{ ...infoSuscripcion, idSuscripcion: idSuscripcion }}
                    procesando={procesandoPago}
                    onConfirmar={handleConfirmarPagoManual}
                    estadoPago={estadoPagoSuscripcion || { dSaldoRestante: 0, dSumaPagos: 0, bTodasOrdensPagadas: false }}
                />
            )}
        </>
    );
};

SubscriptionTitle.propTypes = {
    idSuscripcion: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    infoSuscripcion: PropTypes.object,
    onRefreshInfo: PropTypes.func
};

export default SubscriptionTitle;